import React, { useEffect, useMemo, useState } from "react";
import myToast from "../Utils/MyToast";
import "./CalendarView.css";

const DAYS_COUNT = 42;
const WEEK_DAYS_7 = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEK_DAYS_14 = [
  "S",
  "M",
  "T",
  "W",
  "T",
  "F",
  "S",
  "S",
  "M",
  "T",
  "W",
  "T",
  "F",
  "S",
];
const WEEK_DAYS_FULL_14 = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
];

const LEGEND = [
  { key: "taken", label: "Taken" },
  { key: "not-taken", label: "Not Taken" },
  { key: "sunday", label: "Sunday" },
  { key: "holiday", label: "Holiday" },
];

const LEGEND_HOMEWORK = [
  { key: "taken", label: "Assigned" },
  { key: "not-taken", label: "Not Assigned" },
  { key: "sunday", label: "Sunday" },
  { key: "holiday", label: "Holiday" },
];

//Created By Cursor
function sameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

//Created By Cursor
function statusTone(status) {
  const s = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
  if (s === "taken" || s === "assigned") return "taken";
  if (s === "not-taken" || s === "not-assigned") return "not-taken";
  if (s === "sunday") return "sunday";
  if (s === "holiday") return "holiday";
  return "";
}

//Created By Cursor
function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

const CalendarView = ({
  events = [],
  disableFutureDates = false,
  onDayPress = () => {},
  onDayLongPress = () => {},
  onNextPress = () => {},
  onPrevPress = () => {},
  start_date,
  end_date,
  selectedDate = null,
  showLegend = true,
  columns = 14,
  subtitle = "Select a date to view attendance",
  legendVariant = "attendance",
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [cells, setCells] = useState([]);
  const colCount = columns === 7 ? 7 : 14;
  const weekDays = colCount === 7 ? WEEK_DAYS_7 : WEEK_DAYS_14;
  const weekDaysFull = colCount === 7 ? WEEK_DAYS_7 : WEEK_DAYS_FULL_14;
  const legendItems =
    legendVariant === "homework" ? LEGEND_HOMEWORK : LEGEND;

  useEffect(() => {
    const dates = [];
    const calendar = new Date(currentDate);
    calendar.setDate(1);
    const monthStartDay = calendar.getDay();
    calendar.setDate(calendar.getDate() - monthStartDay);

    for (let i = 0; i < DAYS_COUNT; i++) {
      dates.push(new Date(calendar));
      calendar.setDate(calendar.getDate() + 1);
    }
    setCells(dates);
  }, [currentDate, events]);

  const monthLabel = useMemo(
    () =>
      currentDate.toLocaleString("default", {
        month: "long",
        year: "numeric",
      }),
    [currentDate]
  );

  const getStatus = (date) => {
    const pad = (n) => (n > 9 ? n : `0${n}`);
    const key = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}`;
    const entry = events.find((e) => e.DATE === key);
    return entry?.STATUS || null;
  };

  const isOutOfMonth = (date) =>
    date.getMonth() !== currentDate.getMonth() ||
    date.getFullYear() !== currentDate.getFullYear();

  const isFutureDay = (date) =>
    disableFutureDates && startOfDay(date) > startOfDay(new Date());

  const goPrev = () => {
    const prevMonthDate = new Date(currentDate);
    prevMonthDate.setMonth(prevMonthDate.getMonth() - 1);

    if (start_date) {
      const start = new Date(start_date);
      if (
        prevMonthDate.getFullYear() < start.getFullYear() ||
        (prevMonthDate.getFullYear() === start.getFullYear() &&
          prevMonthDate.getMonth() < start.getMonth())
      ) {
        myToast.notify_warning("Out of session");
        return;
      }
    }

    setCurrentDate(prevMonthDate);
    onPrevPress();
  };

  const goNext = () => {
    const next = new Date(currentDate);
    const nextMonth = next.getMonth() + 1;
    const nextYear = next.getFullYear();

    if (end_date) {
      const end = new Date(end_date);
      if (
        nextYear > end.getFullYear() ||
        (nextYear === end.getFullYear() && next.getMonth() >= end.getMonth())
      ) {
        myToast.notify_warning("Out of session");
        return;
      }
    }

    setCurrentDate(new Date(nextYear, nextMonth, 1));
    onNextPress();
  };

  const handleDayClick = (date, disabled) => {
    if (disabled) return;
    onDayPress(date);
  };

  return (
    <div
      className={`cv-calendar${
        colCount === 7 ? " cv-calendar--cols-7" : " cv-calendar--cols-14"
      }`}
    >
      <div className="cv-header">
        <button
          type="button"
          className="cv-nav cv-nav--prev"
          onClick={goPrev}
          aria-label="Previous month"
        >
          <svg
            className="cv-nav__icon"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M15.5 5.5 9 12l6.5 6.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className="cv-header__center">
          <div className="cv-header__title">{monthLabel}</div>
          {subtitle ? (
            <div className="cv-header__sub">{subtitle}</div>
          ) : null}
        </div>
        <button
          type="button"
          className="cv-nav cv-nav--next"
          onClick={goNext}
          aria-label="Next month"
        >
          <svg
            className="cv-nav__icon"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M8.5 5.5 15 12l-6.5 6.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="cv-weekdays" aria-hidden>
        {weekDays.map((d, i) => (
          <div
            key={`${weekDaysFull[i]}-${i}`}
            title={weekDaysFull[i]}
            className={`cv-weekday${
              weekDaysFull[i] === "Sun" ? " cv-weekday--sun" : ""
            }${
              colCount === 14 && (i === 6 || i === 13)
                ? " cv-weekday--edge"
                : ""
            }`}
          >
            {d}
          </div>
        ))}
      </div>

      <div className="cv-grid" role="grid" aria-label={monthLabel}>
        {cells.map((date, idx) => {
          const status = getStatus(date);
          const tone = statusTone(status);
          const out = isOutOfMonth(date);
          const today = sameDay(date, new Date());
          const future = isFutureDay(date);
          const selected = selectedDate ? sameDay(date, selectedDate) : false;
          const disabled = out || future;
          const col = idx % colCount;

          const classes = [
            "cv-day",
            tone ? `cv-day--${tone}` : "",
            out ? "cv-day--out" : "",
            today ? "cv-day--today" : "",
            future ? "cv-day--future" : "",
            selected ? "cv-day--selected" : "",
            disabled ? "is-disabled" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div
              className={`cv-cell${
                colCount === 14 && (col === 6 || col === 13)
                  ? " cv-cell--edge"
                  : ""
              }`}
              key={`${date.toISOString()}-${idx}`}
            >
              <button
                type="button"
                className={classes}
                disabled={disabled}
                aria-label={`${date.toDateString()}${
                  status ? `, ${status}` : ""
                }`}
                aria-pressed={selected}
                onClick={() => handleDayClick(date, disabled)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  if (!disabled) onDayLongPress(date);
                }}
              >
                <span className="cv-day__num">{date.getDate()}</span>
              </button>
            </div>
          );
        })}
      </div>

      {showLegend ? (
        <div className="cv-legend" aria-label="Status legend">
          {legendItems.map((item) => (
            <span
              key={item.key}
              className={`cv-legend__item cv-legend__item--${item.key}`}
            >
              <i className="cv-legend__swatch" aria-hidden />
              {item.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default CalendarView;
